/**
 * Billing API client.
 * Never sends uid, Price IDs, amounts, success/cancel URLs, or metadata.
 * Never logs tokens, Checkout URLs, or Stripe ids.
 */

import { rememberUpgrade } from "./pendingUpgrade";
import { DISPLAY_OFFERS } from "./planCatalog";
import { billingReturnTrace } from "./appReturn";
import { BILLING_PATHS, billingWebConfig, isBillingApiConfigured } from "./billingConfig";
import { getAppCheckToken, getIdToken } from "./firebaseAuth";
import { captureScope, createScopedAbortController, isCurrentScope } from "../auth/authSession";

export function newUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function billingError(message, extra = {}) {
  const error = new Error(message);
  Object.assign(error, extra);
  return error;
}

export function buildCheckoutBody(offerKey) {
  const key = String(offerKey || "").trim();
  if (!key) {
    throw billingError("offerKey_required", { code: "offerKey_required" });
  }
  return { offerKey: key, channel: "web" };
}

export function buildPortalBody(offerKey) {
  if (offerKey && !DISPLAY_OFFERS.some(offer => offer.offerKey === offerKey)) {
    throw billingError('invalid_offer', {code:'invalid_offer'});
  }
  return { channel: "web", ...(offerKey ? {offerKey} : {}) };
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function billingFetch(path, options = {}) {
  if (!isBillingApiConfigured()) {
    throw billingError("billing_api_unconfigured", { code: "billing_api_unconfigured" });
  }
  const scope = options.scope || captureScope();
  const controller = createScopedAbortController();
  const timeoutMs = options.timeoutMs ?? 20000;
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const send = async (forceRefresh) => {
    const token = await getIdToken({ forceRefresh });
    if (!token) throw billingError("signed_out", { code: "signed_out", status: 401 });
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    };
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
    if (options.ifNoneMatch) headers["If-None-Match"] = options.ifNoneMatch;
    if (options.requireAppCheck) {
      const appCheck = await getAppCheckToken();
      if (appCheck) headers["X-Firebase-AppCheck"] = appCheck;
    }
    if (!isCurrentScope(scope)) throw billingError('stale_session', {code:'stale_session'});
    return fetch(`${billingWebConfig.billingApiBaseUrl}${path}`, {
      method: options.method || "GET",
      headers,
      credentials: "omit",
      cache: "no-store",
      signal: controller.signal,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  };

  try {
    let response = await send(false);
    if (response.status === 401) {
      response = await send(true);
    }
    if (!isCurrentScope(scope)) {
      throw billingError("stale_session", { code: "stale_session" });
    }
    return { response, scope };
  } catch (err) {
    if (err?.code) throw err;
    if (err?.name === "AbortError") {
      throw billingError("request_aborted", { code: "request_aborted", abort: true });
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchEntitlement(options = {}) {
  const { response, scope } = await billingFetch(options.reconcile ? BILLING_PATHS.entitlementSync : BILLING_PATHS.entitlement, {
    ...options,
    ...(options.reconcile ? {method:'POST', body:{}} : {}),
    headers: {...options.headers, ...(options.correlationId ? {'X-Correlation-ID':options.correlationId} : {})},
    requireAppCheck: true,
  });

  if (response.status === 304) {
    return { notModified: true, entitlement: null, etag: options.ifNoneMatch, status: "ready", scope };
  }

  const json = await parseJsonSafe(response);
  if (!isCurrentScope(scope)) {
    throw billingError("stale_session", { code: "stale_session" });
  }
  if (!response.ok) {
    throw billingError(json?.error?.message || "entitlement_failed", {
      code: json?.error?.code || `http_${response.status}`,
      status: response.status,
    });
  }

  return {
    notModified: false,
    status: json?.status || (json?.entitlement ? "ready" : "unknown"),
    entitlement: json?.entitlement || null,
    uxGates: json?.uxGates || null,
    etag: response.headers.get("etag"),
    correlationId: json?.correlationId,
    scope,
  };
}

export async function createCheckoutSession({ offerKey, idempotencyKey, scope, signal, correlationId }) {
  const key = idempotencyKey || newUuid();
  const correlation = correlationId || `corr_${newUuid().replace(/-/g,'')}`;
  const body = buildCheckoutBody(offerKey);
  billingReturnTrace('checkout_start', {correlationId:correlation, offerKey});
  const { response, scope: usedScope } = await billingFetch(BILLING_PATHS.checkoutSessions, {
    method: "POST",
    body,
    idempotencyKey: key,
    headers: {"X-Correlation-ID":correlation},
    requireAppCheck: false,
    scope,
    signal,
  });
  const json = await parseJsonSafe(response);
  if (!isCurrentScope(usedScope)) {
    throw billingError("stale_session", { code: "stale_session" });
  }
  if (!response.ok) {
    throw billingError(json?.error?.message || "checkout_failed", {
      code: json?.error?.code || `http_${response.status}`,
      status: response.status,
      reason: json?.error?.reason,
    });
  }

  if (json?.result === 'manage_existing_subscription') {
    const portal = await createPortalSession({offerKey, scope:usedScope, signal,
      headers:{'X-Correlation-ID':correlation}});
    rememberUpgrade(usedScope, offerKey, correlation);
    return {...portal, correlationId:correlation, idempotencyKey:key};
  }
  if (json?.checkoutUrl) rememberUpgrade(usedScope, offerKey, correlation);
  return {...json, correlationId:correlation, idempotencyKey:key};
}

export async function createPortalSession(options = {}) {
  const { response, scope } = await billingFetch(BILLING_PATHS.portalSessions, {
    method: "POST",
    body: buildPortalBody(options.offerKey),
    requireAppCheck: false,
    ...options,
  });
  const json = await parseJsonSafe(response);
  if (!isCurrentScope(scope)) {
    throw billingError("stale_session", { code: "stale_session" });
  }
  if (!response.ok) {
    throw billingError(json?.error?.message || "portal_failed", {
      code: json?.error?.code || `http_${response.status}`,
      status: response.status,
    });
  }
  return json;
}
